import {VehicleListStatusBadge} from "@/src/modules/vehicles/components/vehicle-list-status-badge";
import {cn} from "@/lib/utils";

import {
  getAuthorizationCoverageDisplay,
  getAuthorizationPlaceholderDisplay,
  shouldShowCheckAuthorizationForVehicle,
} from "../lib/vehicle-check-authorization";
import type {AuthorizationCoverage, VehicleCheck} from "../types";

type VehicleCheckAuthorizationStatusProps = {
  check?: VehicleCheck | null;
  vehicleId?: number | null;
  safelistingCoverage?: AuthorizationCoverage | null;
  permanentParkingCoverage?: AuthorizationCoverage | null;
  className?: string;
  showPlaceholders?: boolean;
};

function CoverageCell({
  label,
  coverage,
  showPlaceholder,
}: {
  label: string;
  coverage?: AuthorizationCoverage | null;
  showPlaceholder: boolean;
}) {
  const display =
    getAuthorizationCoverageDisplay(coverage) ??
    (showPlaceholder ? getAuthorizationPlaceholderDisplay() : null);

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-muted-foreground text-xs font-medium">{label}</span>
      {display ? (
        <VehicleListStatusBadge display={display} />
      ) : (
        <span className="text-muted-foreground text-xs">—</span>
      )}
    </div>
  );
}

export function VehicleCheckAuthorizationStatus({
  check,
  vehicleId,
  safelistingCoverage,
  permanentParkingCoverage,
  className,
  showPlaceholders = false,
}: VehicleCheckAuthorizationStatusProps) {
  const resolvedVehicleId = vehicleId ?? check?.vehicle_id ?? check?.suggested_vehicle_id ?? null;
  const canShow =
    check && resolvedVehicleId != null
      ? shouldShowCheckAuthorizationForVehicle(check, resolvedVehicleId)
      : safelistingCoverage != null || permanentParkingCoverage != null;

  const safelisting =
    safelistingCoverage ?? (canShow ? check?.safelisting_coverage_at_check : undefined);
  const permanentParking =
    permanentParkingCoverage ??
    (canShow ? check?.permanent_parking_coverage_at_check : undefined);

  const placeholders = showPlaceholders && !canShow;

  return (
    <div className={cn("grid grid-cols-2 gap-3", className)}>
      <CoverageCell
        label="Safelisting"
        coverage={canShow || safelistingCoverage != null ? safelisting : undefined}
        showPlaceholder={placeholders}
      />
      <CoverageCell
        label="Permanent parking"
        coverage={canShow || permanentParkingCoverage != null ? permanentParking : undefined}
        showPlaceholder={placeholders}
      />
    </div>
  );
}
